import { useCallback, useEffect, useRef } from 'react';
import { useProjectId } from 'store/project';
import {
  showErrorToastWithMessage,
  showGenericErrorToast,
} from 'services/toaster';
import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { InspectionStatus } from 'shared/domain/inspection/inspectionModel';
import {
  buildProtocolAnswerApplicabilityUpdateOutDto,
  buildProtocolAnswerUpdateOutDto,
  findProtocolAnswerId,
} from 'shared/domain/inspection/mapping/buildProtocolAnswerUpdateOutDto';
import { parseAtomicFormKey } from 'shared/domain/inspection/mapping/parseProtocolAtomicFormKey';
import {
  isInspectionHeaderFieldKey,
  toSingleFieldOutDto,
} from 'shared/domain/inspection/mapping/toSingleFieldOutDto';
import { getProtocolItemEkComment } from 'shared/domain/inspection/protocolItemAccessors';
import {
  findProtocolItemByChecklistItemId,
  findProtocolItemId,
} from 'shared/domain/inspection/protocolItemResolver';
import {
  resolveProtocolSectionId,
  resolveSectionBatchKeyId,
} from 'shared/domain/inspection/protocolSectionResolver';
import { InspectionOutDto } from 'shared/dtos/out/inspection';
import { InspectionInDto } from 'shared/dtos/in/inspection';
import { ProtocolItemOutDto } from 'shared/dtos/out/protocolItem';
import {
  chooseErrorMessage,
  getErrorMessage,
  getErrorToastHideDelay,
  isAbortError,
} from 'shared/utils/errorHandling';
import { logError } from 'setup/logger/logError';
import { isChannelRequestError } from 'shared/channels';
import { getInspectionStoreState, useInspectionStore } from '../store';

function beginAbortableRequest(
  controllers: Map<string, AbortController>,
  key: string
): AbortSignal {
  controllers.get(key)?.abort();

  const controller = new AbortController();
  controllers.set(key, controller);
  return controller.signal;
}

function endAbortableRequest(
  controllers: Map<string, AbortController>,
  key: string,
  signal: AbortSignal
): void {
  const current = controllers.get(key);
  if (current?.signal === signal) {
    controllers.delete(key);
  }
}

function shouldIgnoreAbortableError(
  signal: AbortSignal,
  error: unknown
): boolean {
  return signal.aborted || isAbortError(error);
}

/**
 * Prefers a precise, translated toast for known backend validation errors
 * (e.g. "answers must be filled before completing inspection") over the
 * generic "something went wrong" message.
 */
function displaySaveErrorToaster(error: unknown, logMessage: string): void {
  logError(logMessage, error);

  const messageId = isChannelRequestError(error)
    ? error.messageId ?? chooseErrorMessage(error.cause)
    : chooseErrorMessage(error);
  if (messageId) {
    showErrorToastWithMessage(messageId, undefined, getErrorToastHideDelay(messageId));
    return;
  }

  showGenericErrorToast();
}

/**
 * 'skipped' covers legitimate no-ops (atomic save disabled, inspection
 * deleted/missing, superseded by a newer save for the same channel) - only
 * 'failed' represents an actual rejected/errored save. Callers that roll back
 * optimistic UI must only do so on 'failed', not 'skipped'.
 */
export type SaveOutcome = 'saved' | 'skipped' | 'failed';

export function useAtomicPersist(): {
  persistField: (formKey: string, value: unknown) => void;
  saveInspectionStatus: (isCompleted: boolean) => Promise<SaveOutcome>;
  saveProtocolAnswer: (
    checklistItemId: string,
    checklistAnswerId: string,
    answerType: AnswerType,
    value: unknown
  ) => Promise<void>;
  saveProtocolAnswerApplicability: (
    checklistItemId: string,
    checklistAnswerId: string,
    answerType: AnswerType,
    isApplicable: boolean
  ) => Promise<void>;
  saveProtocolSectionAnswersApplicability: (
    sectionId: string,
    isApplicable: boolean
  ) => Promise<SaveOutcome>;
} {
  const projectId = useProjectId();
  const atomicSaveEnabled = useInspectionStore.use.atomicSaveEnabled();
  const putInspection = useInspectionStore.use.putInspection();
  const putProtocol = useInspectionStore.use.putProtocol();
  const putProtocolAnswer = useInspectionStore.use.putProtocolAnswer();
  const putProtocolSection = useInspectionStore.use.putProtocolSection();
  const putProtocolSectionAnswersBatch =
    useInspectionStore.use.putProtocolSectionAnswersBatch();
  const abortControllersRef = useRef(new Map<string, AbortController>());

  useEffect(() => {
    const controllers = abortControllersRef.current;

    return () => {
      for (const controller of controllers.values()) {
        controller.abort();
      }
      controllers.clear();
    };
  }, []);

  const savePartialInspection = useCallback(
    async (
      partialDto: Partial<InspectionOutDto>,
      abortChannel: string,
      verifyResponse?: (response: InspectionInDto) => void
    ): Promise<SaveOutcome> => {
      if (!atomicSaveEnabled) {
        return 'skipped';
      }

      const inspectionModel = getInspectionStoreState().inspectionModel;
      if (inspectionModel?.deleted) {
        return 'skipped';
      }

      const inspectionId = inspectionModel?._id;
      if (!inspectionId) {
        return 'skipped';
      }

      // Keyed per field/channel (not just per inspection) - otherwise unrelated
      // concurrent saves (e.g. an autoSelect-triggered site save landing right
      // after the user marks the inspection Completed) abort each other and
      // the loser is silently dropped (aborts are intentionally not toasted).
      const abortKey = `inspection:${inspectionId}:${abortChannel}`;
      const signal = beginAbortableRequest(abortControllersRef.current, abortKey);

      try {
        const response = await putInspection(
          inspectionId,
          partialDto,
          signal,
          projectId
        );

        // A 200 response doesn't guarantee the backend actually applied the
        // change (e.g. a business rule can silently keep the previous value)
        // - verify it explicitly so a rejected change surfaces as an error
        // instead of only reappearing after the next page reload.
        verifyResponse?.(response);
        return 'saved';
      } catch (error: unknown) {
        if (shouldIgnoreAbortableError(signal, error)) {
          return 'skipped';
        }

        displaySaveErrorToaster(error, getErrorMessage(error) || 'useAtomicPersist');
        return 'failed';
      } finally {
        endAbortableRequest(abortControllersRef.current, abortKey, signal);
      }
    },
    [atomicSaveEnabled, projectId, putInspection]
  );

  const saveInspectionField = useCallback(
    async (formKey: string, value: unknown): Promise<void> => {
      if (!isInspectionHeaderFieldKey(formKey)) {
        return;
      }

      const partialDto = toSingleFieldOutDto(
        formKey,
        value,
        getInspectionStoreState().inspectionModel
      );
      if (!partialDto) {
        return;
      }

      await savePartialInspection(partialDto, `field:${formKey}`);
    },
    [savePartialInspection]
  );

  const saveInspectionStatus = useCallback(
    async (isCompleted: boolean): Promise<SaveOutcome> => {
      if (!atomicSaveEnabled) {
        return 'skipped';
      }

      // Backend PUT ignores `isCompleted` since PT-6688; requires `status` enum.
      const status = isCompleted
        ? InspectionStatus.completed
        : InspectionStatus.inProgress;

      return savePartialInspection({ status }, 'status', (response) => {
        if (response.status !== status) {
          throw new Error(
            'Inspection status change was not applied by the server.'
          );
        }
      });
    },
    [atomicSaveEnabled, savePartialInspection]
  );

  const saveProtocolItemComment = useCallback(
    async (protocolItemId: string, comment: string): Promise<void> => {
      const inspectionId = getInspectionStoreState().inspectionModel?._id;
      if (!inspectionId) {
        return;
      }

      const currentItem = getInspectionStoreState().inspectionModel?.protocol.find(
        (item) => item._id === protocolItemId
      );
      if (
        currentItem &&
        getProtocolItemEkComment(currentItem) === comment
      ) {
        return;
      }

      const body: ProtocolItemOutDto = { comment };
      const abortKey = `protocol:${protocolItemId}`;
      const signal = beginAbortableRequest(abortControllersRef.current, abortKey);

      try {
        await putProtocol(
          inspectionId,
          protocolItemId,
          body,
          signal,
          projectId
        );
      } catch (error: unknown) {
        if (shouldIgnoreAbortableError(signal, error)) {
          return;
        }

        displaySaveErrorToaster(error, getErrorMessage(error) || 'useAtomicPersist');
      } finally {
        endAbortableRequest(abortControllersRef.current, abortKey, signal);
      }
    },
    [projectId, putProtocol]
  );

  const saveProtocolAnswer = useCallback(
    async (
      checklistItemId: string,
      checklistAnswerId: string,
      answerType: AnswerType,
      value: unknown
    ): Promise<void> => {
      if (!atomicSaveEnabled) {
        return;
      }

      const inspectionModel = getInspectionStoreState().inspectionModel;
      if (inspectionModel?.deleted) {
        return;
      }

      const inspectionId = inspectionModel?._id;
      if (!inspectionId || !inspectionModel) {
        return;
      }

      const protocolItem = findProtocolItemByChecklistItemId(
        inspectionModel.protocol,
        checklistItemId
      );

      if (!protocolItem?._id) {
        return;
      }

      const protocolAnswerId = findProtocolAnswerId(
        protocolItem,
        checklistAnswerId
      );

      if (!protocolAnswerId) {
        return;
      }

      const body = buildProtocolAnswerUpdateOutDto(answerType, value);
      if (!body) {
        return;
      }

      const abortKey = `answer:${checklistItemId}:${checklistAnswerId}`;
      const signal = beginAbortableRequest(abortControllersRef.current, abortKey);

      try {
        await putProtocolAnswer(
          inspectionId,
          protocolItem._id,
          protocolAnswerId,
          body,
          signal,
          projectId
        );
      } catch (error: unknown) {
        if (shouldIgnoreAbortableError(signal, error)) {
          return;
        }

        displaySaveErrorToaster(error, getErrorMessage(error) || 'useAtomicPersist');
      } finally {
        endAbortableRequest(abortControllersRef.current, abortKey, signal);
      }
    },
    [atomicSaveEnabled, projectId, putProtocolAnswer]
  );

  const saveProtocolAnswerApplicability = useCallback(
    async (
      checklistItemId: string,
      checklistAnswerId: string,
      answerType: AnswerType,
      isApplicable: boolean
    ): Promise<void> => {
      if (!atomicSaveEnabled) {
        return;
      }

      const inspectionModel = getInspectionStoreState().inspectionModel;
      if (inspectionModel?.deleted) {
        return;
      }

      const inspectionId = inspectionModel?._id;
      if (!inspectionId) {
        return;
      }

      const protocolItem = findProtocolItemByChecklistItemId(
        inspectionModel.protocol,
        checklistItemId
      );
      const protocolAnswerId = findProtocolAnswerId(
        protocolItem,
        checklistAnswerId
      );
      if (!protocolItem?._id || !protocolAnswerId) {
        return;
      }

      const abortKey = `answer-applicability:${checklistItemId}:${checklistAnswerId}`;
      const signal = beginAbortableRequest(abortControllersRef.current, abortKey);

      try {
        await putProtocolAnswer(
          inspectionId,
          protocolItem._id,
          protocolAnswerId,
          buildProtocolAnswerApplicabilityUpdateOutDto(answerType, isApplicable),
          signal,
          projectId
        );
      } catch (error: unknown) {
        if (shouldIgnoreAbortableError(signal, error)) {
          return;
        }

        displaySaveErrorToaster(error, getErrorMessage(error) || 'useAtomicPersist');
      } finally {
        endAbortableRequest(abortControllersRef.current, abortKey, signal);
      }
    },
    [atomicSaveEnabled, projectId, putProtocolAnswer]
  );

  const saveProtocolSectionAnswersApplicability = useCallback(
    async (sectionId: string, isApplicable: boolean): Promise<SaveOutcome> => {
      if (!atomicSaveEnabled) {
        return 'skipped';
      }

      const inspectionModel = getInspectionStoreState().inspectionModel;
      if (inspectionModel?.deleted) {
        return 'skipped';
      }

      const inspectionId = inspectionModel?._id;
      if (!inspectionId) {
        return 'skipped';
      }

      const batchSectionId = resolveSectionBatchKeyId(
        inspectionModel,
        sectionId
      );

      const abortKey = `section-answers-applicability:${batchSectionId}`;
      const signal = beginAbortableRequest(
        abortControllersRef.current,
        abortKey
      );

      try {
        await putProtocolSectionAnswersBatch(
          inspectionId,
          batchSectionId,
          { isApplicable },
          signal,
          projectId
        );
        return 'saved';
      } catch (error: unknown) {
        if (shouldIgnoreAbortableError(signal, error)) {
          return 'skipped';
        }

        displaySaveErrorToaster(error, getErrorMessage(error) || 'useAtomicPersist');
        return 'failed';
      } finally {
        endAbortableRequest(abortControllersRef.current, abortKey, signal);
      }
    },
    [atomicSaveEnabled, projectId, putProtocolSectionAnswersBatch]
  );

  const saveProtocolSectionComment = useCallback(
    async (sectionId: string, comment: string): Promise<void> => {
      const inspectionModel = getInspectionStoreState().inspectionModel;
      const inspectionId = inspectionModel?._id;
      const resolvedSectionId = resolveProtocolSectionId(
        inspectionModel,
        sectionId
      );

      if (!inspectionId || !resolvedSectionId) {
        return;
      }

      const currentSection = inspectionModel?.sections.find(
        (section) => section._id === resolvedSectionId
      );
      if (currentSection && (currentSection.comment ?? '') === comment) {
        return;
      }

      const abortKey = `section:${resolvedSectionId}`;
      const signal = beginAbortableRequest(abortControllersRef.current, abortKey);

      try {
        await putProtocolSection(
          inspectionId,
          resolvedSectionId,
          { comment },
          projectId
        );
      } catch (error: unknown) {
        if (shouldIgnoreAbortableError(signal, error)) {
          return;
        }

        displaySaveErrorToaster(error, getErrorMessage(error) || 'useAtomicPersist');
      } finally {
        endAbortableRequest(abortControllersRef.current, abortKey, signal);
      }
    },
    [projectId, putProtocolSection]
  );

  const persistField = useCallback(
    (formKey: string, value: unknown): void => {
      if (!atomicSaveEnabled) {
        return;
      }

      const parsed = parseAtomicFormKey(formKey);
      if (!parsed) {
        return;
      }

      void (async () => {
        try {
          switch (parsed.kind) {
            case 'inspectionHeader':
              await saveInspectionField(parsed.field, value);
              break;

            case 'protocolSectionComment':
              await saveProtocolSectionComment(
                parsed.sectionId,
                typeof value === 'string' ? value : ''
              );
              break;

            case 'protocolItemComment': {
              const protocolItemId = findProtocolItemId(
                getInspectionStoreState().inspectionModel?.protocol,
                parsed.checklistItemId
              );

              if (protocolItemId) {
                await saveProtocolItemComment(
                  protocolItemId,
                  typeof value === 'string' ? value : ''
                );
              }
              break;
            }
          }
        } catch (error: unknown) {
          displaySaveErrorToaster(error, getErrorMessage(error) || 'useAtomicPersist');
        }
      })();
    },
    [
      atomicSaveEnabled,
      saveInspectionField,
      saveProtocolItemComment,
      saveProtocolSectionComment,
    ]
  );

  return {
    persistField,
    saveInspectionStatus,
    saveProtocolAnswer,
    saveProtocolAnswerApplicability,
    saveProtocolSectionAnswersApplicability,
  };
}
